import { Button, Input, Select, toast } from "@gotts.ai/ui";
import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatEther } from "viem";

import { useEvmBackend } from "../../context";
import { useRpcRequest } from "../../hooks/useRpcRequest";
import { formatDisplayAmount } from "../../lib/format";

import type { ChainControlSharedProps } from "./types";

const PRESETS = [
  { label: "100", value: "100" },
  { label: "1,000", value: "1000" },
  { label: "10,000", value: "10000" },
  { label: "Drain to 0", value: "0" },
];

export function SetBalanceCard({
  loading,
  withLoading,
  onAction,
  blockNumber,
}: ChainControlSharedProps) {
  const { request } = useRpcRequest();
  const { accounts } = useEvmBackend();

  const accountOptions = useMemo(
    () => [
      ...accounts.map((a) => ({
        label: `${a.label} (${a.address.slice(0, 6)}...${a.address.slice(-4)})`,
        value: a.address,
      })),
      { label: "Other", value: "__other__" },
    ],
    [accounts],
  );

  const [accountSelect, setAccountSelect] = useState<string>(
    accounts[0]?.address ?? "",
  );
  const [customAddress, setCustomAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [currentBalance, setCurrentBalance] = useState<string | null>(null);

  const address = accountSelect === "__other__" ? customAddress : accountSelect;

  const fetchBalance = useCallback(async () => {
    if (!address) {
      setCurrentBalance(null);
      return;
    }
    try {
      const hex = (await request("eth_getBalance", [
        address,
        "latest",
      ])) as string;
      setCurrentBalance(formatEther(BigInt(hex)));
    } catch {
      setCurrentBalance(null);
    }
  }, [address, request]);

  useEffect(() => {
    void fetchBalance();
  }, [fetchBalance, blockNumber]);

  const accountLabel =
    accounts.find((a) => a.address === address)?.label ??
    `${address.slice(0, 6)}...${address.slice(-4)}`;

  async function handleSetBalance() {
    if (!address || amount === "") return;
    const beforeFormatted =
      currentBalance !== null
        ? formatDisplayAmount(currentBalance).display
        : "?";
    await withLoading("setBalance", async () => {
      const weiHex =
        "0x" + BigInt(Math.floor(parseFloat(amount) * 1e18)).toString(16);
      await request("anvil_setBalance", [address, weiHex]);
      toast.success(
        `${accountLabel}: ${beforeFormatted} \u2192 ${formatDisplayAmount(amount).display} ETH`,
      );
      onAction?.(`Set ${accountLabel} to ${amount} ETH`);
    });
  }

  return (
    <div className="space-y-4">
      <Select
        options={accountOptions}
        value={accountSelect}
        onValueChange={setAccountSelect}
        placeholder="Select account"
      />
      {accountSelect === "__other__" && (
        <Input
          type="text"
          placeholder="0x... custom address"
          value={customAddress}
          onChange={(e) => {
            setCustomAddress(e.target.value);
          }}
          className="w-full"
        />
      )}

      {/* Balance display — hero serif number */}
      {currentBalance !== null && address && (
        <div className="flex items-baseline gap-2">
          <span className="hero-value text-xl">
            {formatDisplayAmount(currentBalance).display}
          </span>
          <span className="font-mono text-xs text-[var(--color-text-muted)]">
            ETH
          </span>
        </div>
      )}

      {/* Amount input */}
      <div className="flex items-center gap-2">
        <Input
          type="number"
          placeholder="New balance"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
          }}
          className="flex-1"
        />
        <span className="font-mono text-xs text-[var(--color-text-muted)]">
          ETH
        </span>
        <Button
          variant="gold"
          size="sm"
          onClick={handleSetBalance}
          loading={loading === "setBalance"}
          disabled={loading !== null || !address || amount === ""}
        >
          Set
        </Button>
      </div>

      {/* Transition preview */}
      {currentBalance !== null && amount && address && (
        <div className="flex items-center gap-2 rounded-sm bg-[rgba(255,255,255,0.02)] px-3 py-2">
          <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
            {formatDisplayAmount(currentBalance).display} ETH
          </span>
          <ArrowRight size={12} className="text-[var(--color-gold)]/50" />
          <span className="font-mono text-[11px] text-[var(--color-text)]">
            {formatDisplayAmount(amount).display} ETH
          </span>
        </div>
      )}

      {/* Presets */}
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.filter((p) => p.value !== "0").map((p) => (
          <button
            key={p.value}
            type="button"
            className="btn-percentage-pill"
            onClick={() => {
              setAmount(p.value);
            }}
            disabled={loading !== null}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          className="btn-percentage-pill !border-[var(--color-error)]/30 !text-[var(--color-error)]"
          onClick={() => {
            setAmount("0");
          }}
          disabled={loading !== null}
        >
          Drain to 0
        </button>
      </div>

      <p className="text-[9px] text-[var(--color-text-muted)]">
        Sets the exact balance, not adds to it.
      </p>
    </div>
  );
}
